import { Link, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { MenuItem } from "@mui/material";
import { Badge } from "react-bootstrap";
import Sidebar from "../../../components/sidebar/Sidebar";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { getPackageColor, getPackageName, getPackagePrice, getPackageProfit, getPackageTitle, isAppServicePackage } from "../../../utils/packages";
import "./ViewAccountPage.css";
import { MUI_TABLE } from '../../../utils/muiTableConfig';
import {
  describeBalances,
  totalUsd,
  fmtRelative,
  explorerUrl,
  WALLET_STATUS_COLOURS,
  INVOICE_STATUS_COLOURS,
} from '../../../utils/wallets';
import { Money, Username, useMask } from '../../../utils/privacyMask';
import { getServiceVendor, getServiceTiers, getServiceColor } from '../../../utils/resellerServices';

/* ── Shared MUI dark sx props ─────────────────────────────────────── */

/* ── Stat card ────────────────────────────────────────────────────── */
function StatCard({ label, value, icon, accent, glow }) {
  return (
    <div className="view-stat-card" style={{ '--accent': accent, '--glow': glow }}>
      <div className="view-stat-label">{label}</div>
      <div className="view-stat-value">{value}</div>
      <i className={`bi ${icon} view-stat-icon`} />
    </div>
  );
}

/* ── Info row ─────────────────────────────────────────────────────── */
function InfoItem({ icon, label, value }) {
  return (
    <div className="info-item">
      <div className="info-label"><i className={`bi ${icon}`} />{label}</div>
      <div className={`info-value ${!value ? 'muted' : ''}`}>{value || 'Not provided'}</div>
    </div>
  );
}

/* ── Tabs ─────────────────────────────────────────────────────────── */
// Resellers buy credits rather than holding lines, so subscriptions, referrals
// and points cannot apply to them; their tabs are replaced rather than left empty.
const RESELLER_TABS = [
  { id: 'overview',     label: 'Overview',         icon: 'bi-speedometer2' },
  { id: 'credits',      label: 'Credit Purchases', icon: 'bi-coin' },
  { id: 'services',     label: 'Services',         icon: 'bi-diagram-3-fill' },
  { id: 'transactions', label: 'Transactions',     icon: 'bi-receipt' },
];

const TABS = [
  { id: 'overview',      label: 'Overview',      icon: 'bi-speedometer2' },
  { id: 'transactions',  label: 'Transactions',  icon: 'bi-receipt' },
  { id: 'subscriptions', label: 'Subscriptions', icon: 'bi-bookmark-star-fill' },
  { id: 'referrals',     label: 'Referrals',     icon: 'bi-share-fill' },
  { id: 'activity',      label: 'Activity',      icon: 'bi-lightning-charge-fill' },
  { id: 'points',        label: 'Points',        icon: 'bi-star-fill' },
  { id: 'wallets',       label: 'Wallets',       icon: 'bi-wallet2' },
];

/* ── Service badge ── */
function ServiceBadge({ code }) {
  const color = getServiceColor(code);
  return (
    <Badge bg="" style={{
      background: `${color}22`, color, border: `1px solid ${color}66`,
      fontSize: '0.72rem', fontWeight: 600,
    }}>
      {code} · {getServiceVendor(code)}
    </Badge>
  );
}

/* ── Main ─────────────────────────────────────────────────────────── */
export default function ViewAccountPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { mask } = useMask();

  const [accountData, setAccountData] = useState(null);
  const [accountsMap, setAccountsMap] = useState({});
  const [points, setPoints]           = useState(null);
  const [claimHistory, setClaimHistory] = useState([]);
  const [requests, setRequests]       = useState([]);
  const [issues, setIssues]           = useState([]);
  const [trialFollowups, setTrialFollowups] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [activeTab, setActiveTab]     = useState('overview');

  useEffect(() => {
    setRequests([]);
    setIssues([]);
    setTrialFollowups([]);
    setWallets([]);
    setInvoices([]);
    const fetchData = async () => {
      try {
        setLoading(true);
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const [accRes, allAccRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/account/${id}`, { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/account`, { headers }),
        ]);
        setAccountData(accRes.data);
        const map = {};
        allAccRes.data.forEach(a => { map[a._id] = a.username; });
        setAccountsMap(map);

        // Bot requests, issues and trial follow-ups belong to the Activity tab,
        // which a reseller does not have - none of them can exist for one. Skip
        // the three calls rather than fetch them to render nothing.
        const isResellerAccount = accRes.data.account?.accountType === 'reseller';

        // Fetch requests and issues by discord username (fire-and-forget alongside points)
        const discord = accRes.data.account?.discord;
        if (discord && !isResellerAccount) {
          const API = process.env.REACT_APP_API_URL;
          const enc = encodeURIComponent(discord);
          Promise.all([
            axios.get(`${API}/botdata/requests?username=${enc}`, { headers }).catch(() => ({ data: [] })),
            axios.get(`${API}/botdata/issues?memberUsername=${enc}`, { headers }).catch(() => ({ data: [] })),
          ]).then(([rRes, iRes]) => {
            const raws = Array.isArray(rRes.data) ? rRes.data : [];
            const rawI = Array.isArray(iRes.data) ? iRes.data : [];
            setRequests(raws.filter(r => r.username === discord));
            setIssues(rawI.filter(i => i.memberUsername === discord));
          });
        }

        // Fetch trial follow-ups by numeric Discord ID
        const discordIDForTrials = accRes.data.account?.discordID;
        if (discordIDForTrials && !isResellerAccount) {
          axios.get(
            `${process.env.REACT_APP_API_URL}/trial-followups?discordId=${encodeURIComponent(discordIDForTrials)}`,
            { headers }
          ).then(r => setTrialFollowups(Array.isArray(r.data) ? r.data : [])).catch(() => {});

        // Wallets and invoices for this customer. No API change was needed:
        // fetchWallets already filters on accountID and has an index for it.
        // Fire-and-forget so the page paints without waiting on them.
        axios.get(
          `${process.env.REACT_APP_API_URL}/wallet?accountID=${encodeURIComponent(id)}&limit=200`,
          { headers },
        ).then(r => setWallets(Array.isArray(r.data) ? r.data : [])).catch(() => {});

        axios.get(
          `${process.env.REACT_APP_API_URL}/invoice?accountID=${encodeURIComponent(id)}&limit=200`,
          { headers },
        ).then(r => setInvoices(Array.isArray(r.data) ? r.data : [])).catch(() => {});
        }

        // Look up points: accountID first (UI-added), then discordID fallback (bot-added)
        const accountId = accRes.data.account?._id;
        const discordID = accRes.data.account?.discordID;
        if (accountId) {
          (async () => {
            let ptsDoc = null;
            const API = process.env.REACT_APP_API_URL;
            try {
              const r = await axios.get(`${API}/referralpoints/account/${accountId}`, { headers });
              ptsDoc = r.data;
            } catch {
              if (discordID) {
                const r = await axios.get(`${API}/referralpoints/discord/${discordID}`, { headers }).catch(() => null);
                if (r?.data) ptsDoc = r.data;
              }
            }
            setPoints(ptsDoc);
            if (ptsDoc?.discordId) {
              const r = await axios.get(`${API}/referralpoints/claims/${ptsDoc.discordId}`, { headers }).catch(() => null);
              setClaimHistory(r?.data ?? []);
            }
          })();
        }

        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch account data");
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  /* ── Transaction columns ── */
  const txCols = useMemo(() => [
    { header: 'Date',    accessorKey: 'transactionDate', size: 110, Cell: ({ cell }) => new Date(Number(cell.getValue())).toLocaleDateString() },
    { header: 'Revenue', accessorKey: 'revenue',         size: 100, Cell: ({ cell }) => <span style={{ color: '#34d399', fontWeight: 600 }}><Money value={Number(cell.getValue())} /></span> },
    { header: 'Profit',  accessorKey: 'profit',          size: 100, Cell: ({ cell }) => <span style={{ color: '#38bdf8' }}><Money value={Number(cell.getValue())} /></span> },
    { header: 'Method',  accessorKey: 'paymentMethod',   size: 100, Cell: ({ cell }) => {
      const m = cell.getValue();
      return m ? <span style={{ textTransform: 'capitalize', color: '#94a3b8' }}>{m}</span> : <span style={{ color: '#334155' }}>—</span>;
    }},
    { header: 'Notes', accessorKey: 'notes', Cell: ({ cell }) => <span style={{ color: '#64748b' }}>{cell.getValue() || '—'}</span> },
  ], []);

  /* ── Subscription columns ── */
  const subCols = useMemo(() => [
    { header: 'Username',    accessorKey: 'username',    Cell: ({ cell }) => <span style={{ fontWeight: 600 }}><Username value={cell.getValue()} /></span> },
    { header: 'Password',    accessorKey: 'password' },
    { header: 'Credits',     accessorKey: 'creditCost',  size: 80, Cell: ({ cell }) => cell.getValue() || 0 },
    { header: 'Package',     accessorKey: 'package',     Cell: ({ cell }) => {
      const v = cell.getValue();
      return <Badge bg="" style={{ background: getPackageColor(v), fontSize: '0.72rem' }}>{getPackageTitle(v)} – {getPackageName(v)}</Badge>;
    }},
    { header: 'Paid',        accessorKey: 'paidPrice',    size: 90,  Cell: ({ cell }) => <span style={{ color: '#34d399' }}><Money value={cell.getValue() || 0} /></span> },
    { header: 'Profit',      accessorKey: 'profitReceived', size: 90, Cell: ({ cell }) => <span style={{ color: '#38bdf8' }}><Money value={cell.getValue() || 0} /></span> },
    { header: 'Renewal €',   accessorKey: 'renewalPrice', accessorFn: r => getPackagePrice(r.package), size: 90, Cell: ({ cell }) => <Money value={cell.getValue()} /> },
    { header: 'Renewal P.',  accessorFn: r => getPackageProfit(r.package), size: 90, Cell: ({ cell }) => <Money value={cell.getValue()} /> },
    { header: 'Created',     accessorKey: 'createdDate',  size: 100, Cell: ({ cell }) => new Date(Number(cell.getValue())).toLocaleDateString() },
    { header: 'Expires',     accessorKey: 'expireDate',   size: 100, Cell: ({ cell }) => {
      const days = Math.round((Date.now() - Number(cell.getValue())) / 86400000) - 1;
      return <Badge bg={days < 0 ? 'success' : 'danger'} style={{ fontSize: '0.72rem' }}>{Math.abs(days)}d {days < 0 ? 'left' : 'ago'}</Badge>;
    }},
    { header: 'Status',      accessorKey: 'active',       size: 80,  Cell: ({ cell }) =>
      cell.getValue()
        ? <Badge bg="success" style={{ fontSize: '0.72rem' }}>Active</Badge>
        : <Badge bg="danger"  style={{ fontSize: '0.72rem' }}>Inactive</Badge>
    },
    { header: 'Notes', accessorKey: 'notes', Cell: ({ cell }) => <span style={{ color: '#64748b' }}>{cell.getValue() || '—'}</span> },
  ], []);

  /* ── Split subs into IPTV vs App Services ── */
  const iptv    = useMemo(() => (accountData?.subscriptions ?? []).filter(s => !isAppServicePackage(s.package)), [accountData]);
  const appSubs = useMemo(() => (accountData?.subscriptions ?? []).filter(s =>  isAppServicePackage(s.package)), [accountData]);

  /* ── App Services columns ── */
  const appSubCols = useMemo(() => [
    { header: 'Package', accessorKey: 'package', Cell: ({ cell }) => {
      const v = cell.getValue();
      return <Badge bg="" style={{ background: getPackageColor(v), fontSize: '0.72rem' }}>{getPackageTitle(v)} – {getPackageName(v)}</Badge>;
    }},
    { header: 'Paid',    accessorKey: 'paidPrice',      size: 90,  Cell: ({ cell }) => <span style={{ color: '#34d399' }}><Money value={cell.getValue() || 0} /></span> },
    { header: 'Profit',  accessorKey: 'profitReceived', size: 90,  Cell: ({ cell }) => <span style={{ color: '#38bdf8' }}><Money value={cell.getValue() || 0} /></span> },
    { header: 'Created', accessorKey: 'createdDate',    size: 110, Cell: ({ cell }) => new Date(Number(cell.getValue())).toLocaleDateString() },
    { header: 'Expires', accessorKey: 'expireDate',     size: 110, Cell: ({ cell }) => {
      const days = Math.round((Date.now() - Number(cell.getValue())) / 86400000) - 1;
      return <Badge bg={days < 0 ? 'success' : 'danger'} style={{ fontSize: '0.72rem' }}>{Math.abs(days)}d {days < 0 ? 'left' : 'ago'}</Badge>;
    }},
    { header: 'Status', accessorKey: 'active', size: 80, Cell: ({ cell }) =>
      cell.getValue()
        ? <Badge bg="success" style={{ fontSize: '0.72rem' }}>Active</Badge>
        : <Badge bg="danger"  style={{ fontSize: '0.72rem' }}>Inactive</Badge>
    },
    { header: 'Notes', accessorKey: 'notes', Cell: ({ cell }) => <span style={{ color: '#64748b' }}>{cell.getValue() || '—'}</span> },
  ], []);

  /* ── Reseller credit purchases ── */
  const isReseller = accountData?.account?.accountType === 'reseller';

  const creditPurchases = useMemo(
    () => (accountData?.transactions ?? []).filter(t => t.type === 'reseller-credits'),
    [accountData]
  );

  const creditTotals = useMemo(() => {
    const paid    = creditPurchases.reduce((s, t) => s + (t.revenue || 0), 0);
    const profit  = creditPurchases.reduce((s, t) => s + (t.profit  || 0), 0);
    const credits = creditPurchases.reduce((s, t) => s + (t.resellerCredits?.credits || 0), 0);
    const services = [...new Set(creditPurchases.map(t => t.resellerCredits?.service).filter(Boolean))];
    return { paid, profit, credits, services };
  }, [creditPurchases]);

  const creditCols = useMemo(() => [
    { header: 'Date', accessorKey: 'transactionDate', size: 110,
      Cell: ({ cell }) => new Date(Number(cell.getValue())).toLocaleDateString() },
    { header: 'Service', accessorFn: r => r.resellerCredits?.service ?? '', size: 130,
      Cell: ({ row }) => {
        const code = row.original.resellerCredits?.service;
        return code ? <ServiceBadge code={code} /> : <span style={{ color: 'var(--color-subtle)' }}>—</span>;
      }},
    { header: 'Credits', accessorFn: r => r.resellerCredits?.credits ?? 0, size: 90,
      Cell: ({ cell }) => <span style={{ fontWeight: 600 }}>{mask(Number(cell.getValue()).toLocaleString(), 'number')}</span> },
    { header: 'Paid', accessorKey: 'revenue', size: 100,
      Cell: ({ cell }) => <span style={{ color: '#34d399', fontWeight: 600 }}><Money value={Number(cell.getValue())} /></span> },
    { header: '€/credit', accessorFn: r => r.resellerCredits?.creditRate ?? null, size: 95,
      Cell: ({ cell }) => {
        const v = cell.getValue();
        return v == null ? <span style={{ color: 'var(--color-subtle)' }}>—</span> : <Money value={Number(v)} />;
      }},
    { header: 'Our Cost', accessorFn: r => r.resellerCredits?.costEUR ?? null, size: 100,
      Cell: ({ row }) => {
        const d = row.original.resellerCredits;
        if (!d || d.costEUR == null) return <span style={{ color: 'var(--color-subtle)' }}>—</span>;
        return (
          <span style={{ color: '#f87171' }}>
            <Money value={Number(d.costEUR)} />
            {d.costRate != null && (
              <span style={{ color: 'var(--color-subtle)', fontSize: '0.72rem', marginLeft: 5 }}>
                @{mask(`€${Number(d.costRate).toFixed(2)}`, 'money')}
              </span>
            )}
          </span>
        );
      }},
    { header: 'Profit', accessorKey: 'profit', size: 100,
      Cell: ({ cell }) => <span style={{ color: '#38bdf8', fontWeight: 600 }}><Money value={Number(cell.getValue())} /></span> },
    { header: 'Method', accessorKey: 'paymentMethod', size: 100,
      Cell: ({ cell }) => {
        const m = cell.getValue();
        return m ? <span style={{ textTransform: 'capitalize', color: 'var(--color-muted)' }}>{m}</span> : <span style={{ color: 'var(--color-subtle)' }}>—</span>;
      }},
    { header: 'Notes', accessorKey: 'notes',
      Cell: ({ cell }) => <span style={{ color: 'var(--color-muted)' }}>{cell.getValue() || '—'}</span> },
  ], [mask]);

  /* ── Wallet columns ──
     Renewal wallets are keyed per cycle, so this list doubles as the
     customer's crypto payment history. Newest first. */
  const walletCols = useMemo(() => [
    { header: '#', accessorKey: 'index', size: 70, Cell: ({ cell }) => <span style={{ fontWeight: 700 }}>{cell.getValue()}</span> },
    { header: 'Purpose', accessorKey: 'purpose', size: 110, Cell: ({ cell }) => <span style={{ color: '#94a3b8' }}>{cell.getValue() || 'manual'}</span> },
    { header: 'Cycle', accessorKey: 'renewalCycle', size: 110, Cell: ({ cell }) => <span style={{ color: '#64748b' }}>{cell.getValue() || '—'}</span> },
    {
      header: 'Status', accessorKey: 'status', size: 130,
      Cell: ({ cell }) => {
        const v = cell.getValue() || 'unassigned';
        return <Badge style={{ background: WALLET_STATUS_COLOURS[v] || '#64748b', fontWeight: 600 }}>{v.replace('_', ' ')}</Badge>;
      },
    },
    {
      header: 'Balances', accessorKey: 'balances', size: 180, enableSorting: false,
      Cell: ({ row }) => (row.original.hasFunds
        ? <span style={{ color: '#34d399', fontWeight: 600 }}>{describeBalances(row.original.balances)}</span>
        : <span style={{ color: '#64748b' }}>—</span>),
    },
    { header: 'Last scan', accessorKey: 'lastScanAt', size: 120, Cell: ({ cell }) => <span style={{ color: '#94a3b8' }}>{fmtRelative(cell.getValue())}</span> },
    {
      header: 'Address', accessorKey: 'addresses.eth', size: 200, enableSorting: false,
      Cell: ({ row }) => {
        const address = row.original.addresses?.eth;
        if (!address) return <span style={{ color: '#64748b' }}>—</span>;
        return (
          <a href={explorerUrl('eth', address)} target="_blank" rel="noreferrer" style={{ color: '#818cf8', fontSize: '0.8rem' }}>
            <Username value={`${address.slice(0, 10)}…${address.slice(-6)}`} />
          </a>
        );
      },
    },
  ], []);

  /* ── Invoice columns ── */
  const invCols = useMemo(() => [
    {
      header: 'Status', accessorKey: 'status', size: 110,
      Cell: ({ cell }) => <Badge style={{ background: INVOICE_STATUS_COLOURS[cell.getValue()] || '#64748b', fontWeight: 600 }}>{cell.getValue()}</Badge>,
    },
    { header: 'For', accessorKey: 'label', size: 180, Cell: ({ cell }) => <span style={{ fontSize: '0.85rem' }}>{cell.getValue() || '—'}</span> },
    { header: 'Kind', accessorKey: 'kind', size: 100 },
    { header: 'Owed', accessorKey: 'amountEur', size: 100, Cell: ({ cell }) => <Money value={cell.getValue()} /> },
    { header: 'In crypto', accessorKey: 'amountCrypto', size: 140, Cell: ({ row }) => <span style={{ fontSize: '0.82rem' }}>{row.original.amountCrypto} {row.original.symbol}</span> },
    { header: 'Wallet', accessorKey: 'walletIndex', size: 90, Cell: ({ cell }) => <span style={{ fontWeight: 700 }}>#{cell.getValue()}</span> },
    { header: 'Created', accessorKey: 'createdAt', size: 120, Cell: ({ cell }) => <span style={{ color: '#94a3b8' }}>{fmtRelative(cell.getValue())}</span> },
  ], []);

  /* ── Referral columns ── */
  const refCols = useMemo(() => [
    { header: 'Date',    accessorKey: 'referDate', size: 110, Cell: ({ cell }) => new Date(Number(cell.getValue())).toLocaleDateString() },
    { header: 'Referred', accessorKey: 'refered',  Cell: ({ cell }) => <Username value={accountsMap[cell.getValue()] || cell.getValue()} /> },
    { header: 'Notes',   accessorKey: 'notes',     Cell: ({ cell }) => <span style={{ color: '#64748b' }}>{cell.getValue() || '—'}</span> },
  ], [accountsMap]);

  /* ── Bot Request columns ── */
  const reqCols = useMemo(() => [
    { header: 'Content',  accessorKey: 'content',     Cell: ({ cell }) => <span style={{ color: '#e2e8f0', fontSize: '0.85rem' }}>{cell.getValue() || '—'}</span> },
    { header: 'Type',     accessorKey: 'contentType', size: 100, Cell: ({ cell }) => <span style={{ color: '#94a3b8' }}>{cell.getValue() || '—'}</span> },
    { header: 'Service',  accessorKey: 'service',     size: 100, Cell: ({ cell }) => <span style={{ color: '#94a3b8' }}>{cell.getValue() || '—'}</span> },
    { header: 'Status',   accessorKey: 'status',      size: 100, Cell: ({ cell }) => {
      const s = cell.getValue();
      const color = s === 'approved' ? '#4ade80' : s === 'rejected' ? '#f87171' : '#fbbf24';
      return <Badge bg="" style={{ background: color + '22', color, border: `1px solid ${color}55`, fontSize: '0.72rem' }}>{s}</Badge>;
    }},
    { header: 'Date', accessorKey: 'submittedAt', size: 140, Cell: ({ cell }) => {
      const v = cell.getValue();
      return <span style={{ color: '#64748b' }}>{v ? new Date(v).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}</span>;
    }},
  ], []);

  /* ── Issue columns ── */
  const issueCols = useMemo(() => [
    { header: 'Title',    accessorKey: 'title',    Cell: ({ cell }) => <span style={{ color: '#e2e8f0', fontSize: '0.85rem' }}>{cell.getValue() || '—'}</span> },
    { header: 'Status',   accessorKey: 'status',   size: 100, Cell: ({ cell }) => {
      const s = cell.getValue();
      const color = s === 'resolved' ? '#4ade80' : '#fbbf24';
      return <Badge bg="" style={{ background: color + '22', color, border: `1px solid ${color}55`, fontSize: '0.72rem' }}>{s}</Badge>;
    }},
    { header: 'Created',  accessorKey: 'createdAt',  size: 140, Cell: ({ cell }) => {
      const v = cell.getValue();
      return <span style={{ color: '#64748b' }}>{v ? new Date(v).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}</span>;
    }},
    { header: 'Resolved', accessorKey: 'resolvedAt', size: 140, Cell: ({ cell }) => {
      const v = cell.getValue();
      return <span style={{ color: '#64748b' }}>{v ? new Date(v).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}</span>;
    }},
  ], []);

  /* ── Claim columns ── */
  const claimCols = useMemo(() => [
    { header: 'Date',         accessorKey: 'claimedAt',  size: 140,
      Cell: ({ cell }) => <span style={{ color: '#64748b' }}>{new Date(Number(cell.getValue())).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}</span> },
    { header: 'Reward Type',  accessorKey: 'rewardType',  Cell: ({ cell }) => <span style={{ fontWeight: 600 }}>{cell.getValue()}</span> },
    { header: 'Reward Label', accessorKey: 'rewardLabel', Cell: ({ cell }) => <span style={{ color: '#64748b' }}>{cell.getValue() || '—'}</span> },
    { header: 'Points Cost',  accessorKey: 'pointsCost',  size: 110,
      Cell: ({ cell }) => <span style={{ color: '#EF4444', fontWeight: 600 }}>{mask(`−${cell.getValue()?.toLocaleString()}`, 'number')}</span> },
  ], [mask]);

  /* ── Trial Follow-up columns ── */
  const fuCols = useMemo(() => [
    { header: 'Service',    accessorKey: 'service',      size: 110, Cell: ({ cell }) => {
      const s = cell.getValue();
      const label = s === 'three' ? 'Service Three' : s === 'four' ? 'Service Four' : (s || '—');
      return <span style={{ color: '#94a3b8' }}>{label}</span>;
    }},
    { header: 'Status',     accessorKey: 'status',       size: 120, Cell: ({ cell }) => {
      const s   = cell.getValue();
      const map = { pending: '#fbbf24', interested: '#4ade80', not_interested: '#f87171', dismissed: '#6b7280' };
      const c   = map[s] ?? '#94a3b8';
      return <Badge bg="" style={{ background: c + '22', color: c, border: `1px solid ${c}55`, fontSize: '0.72rem' }}>{s?.replace('_', ' ')}</Badge>;
    }},
    { header: 'Trial End',  accessorKey: 'trialEndTime', size: 120, Cell: ({ cell }) => {
      const v = cell.getValue();
      return <span style={{ color: '#64748b' }}>{v ? new Date(Number(v)).toLocaleDateString() : '—'}</span>;
    }},
    { header: 'Follow-up Sent', accessorKey: 'followUpSent', size: 120, Cell: ({ cell }) =>
      cell.getValue()
        ? <Badge bg="success" style={{ fontSize: '0.72rem' }}>Sent</Badge>
        : <Badge bg="secondary" style={{ fontSize: '0.72rem' }}>Pending</Badge>
    },
    { header: 'Feedback',   accessorKey: 'feedbackReason', Cell: ({ cell }) => <span style={{ color: '#64748b' }}>{cell.getValue() || '—'}</span> },
  ], []);

  /* ── Row actions factory ── */
  const makeActions = (editPath, viewPath) => ({ row }) => [
    <MenuItem key="edit"   onClick={() => navigate(`${editPath}/${row.original._id}`)} sx={{ m: 0, gap: 1 }}>
      <i className="bi bi-pencil-square" style={{ fontSize: '0.9rem', color: '#64748b' }} />Edit
    </MenuItem>,
    <MenuItem key="view"   onClick={() => navigate(`${viewPath}/${row.original._id}`)} sx={{ m: 0, gap: 1 }}>
      <i className="bi bi-eye" style={{ fontSize: '0.9rem', color: '#64748b' }} />View
    </MenuItem>,
    <MenuItem key="delete" onClick={() => {}} sx={{ m: 0, gap: 1, color: '#f87171' }}>
      <i className="bi bi-trash" style={{ fontSize: '0.9rem', color: '#f87171' }} />Delete
    </MenuItem>,
  ];

  const txTable       = useMaterialReactTable({ columns: txCols,      data: accountData?.transactions ?? [], enableRowActions: true, renderRowActionMenuItems: makeActions('/transaction/edit', '/transaction/view'),  initialState: { columnPinning: { right: ['mrt-row-actions'] } }, ...MUI_TABLE });
  const subTable      = useMaterialReactTable({ columns: subCols,     data: iptv,    enableRowActions: true, renderRowActionMenuItems: makeActions('/subscription/edit', '/subscription/view'), initialState: { columnPinning: { right: ['mrt-row-actions'] } }, ...MUI_TABLE });
  const appSubTable   = useMaterialReactTable({ columns: appSubCols,  data: appSubs, enableRowActions: true, renderRowActionMenuItems: makeActions('/subscription/edit', '/subscription/view'), initialState: { columnPinning: { right: ['mrt-row-actions'] } }, ...MUI_TABLE });
  const refTable      = useMaterialReactTable({ columns: refCols,     data: accountData?.referrals   ?? [], enableRowActions: true, renderRowActionMenuItems: makeActions('/referral/edit',     '/referral/view'),     initialState: { columnPinning: { right: ['mrt-row-actions'] } }, ...MUI_TABLE });
  const reqTable      = useMaterialReactTable({ columns: reqCols,     data: requests,    enableRowActions: false, ...MUI_TABLE });
  const issueTable    = useMaterialReactTable({ columns: issueCols,   data: issues,      enableRowActions: false, ...MUI_TABLE });
  const claimTable    = useMaterialReactTable({ columns: claimCols,   data: claimHistory, enableRowActions: false, initialState: { showGlobalFilter: false }, ...MUI_TABLE });
  const fuTable       = useMaterialReactTable({ columns: fuCols,      data: trialFollowups, enableRowActions: false, ...MUI_TABLE });
  const walletTable   = useMaterialReactTable({ columns: walletCols, data: wallets,  enableRowActions: false, initialState: { sorting: [{ id: 'index', desc: true }] }, ...MUI_TABLE });
  const invoiceTable  = useMaterialReactTable({ columns: invCols,    data: invoices, enableRowActions: false, initialState: { sorting: [{ id: 'createdAt', desc: true }] }, ...MUI_TABLE });
  const creditTable   = useMaterialReactTable({ columns: creditCols,  data: creditPurchases, enableRowActions: true, renderRowActionMenuItems: makeActions('/transaction/edit', '/transaction/view'), initialState: { columnPinning: { right: ['mrt-row-actions'] } }, ...MUI_TABLE });

  if (loading) return <LoadingSpinner />;
  if (error || !accountData) return (
    <div className="view-page">
      <div className="view-panel">
        <div style={{ padding: 24, color: '#f87171' }}>
          <i className="bi bi-exclamation-triangle-fill me-2" />
          {error || 'No account data found'}
        </div>
      </div>
    </div>
  );

  const { account, transactions, subscriptions, referrals } = accountData;
  const tabs = isReseller ? RESELLER_TABS : TABS;
  // A reseller has no Subscriptions/Referrals/Points tab, so a tab id carried
  // over from a customer page (or vice versa) falls back to Overview.
  const currentTab = tabs.some(t => t.id === activeTab) ? activeTab : 'overview';
  const totalRevenue = transactions.reduce((s, t) => s + (t.revenue || 0), 0);
  const totalProfit  = transactions.reduce((s, t) => s + (t.profit  || 0), 0);

  return (
    <>
      <Sidebar title="Account Details" />

      <div className="view-page">

        {/* ── Header ── */}
        <div className="view-header">
          <div className="view-header-left">
            <div className="view-avatar">
              <i className="bi bi-person-fill" />
            </div>
            <div>
              <div className="view-title"><Username value={account.username} /></div>
              <div className="view-subtitle">
                {account.email || 'No email'} · Created {new Date(Number(account.createdDate)).toLocaleDateString()}
              </div>
            </div>
          </div>
          <Link to={`/account/edit/${account._id}`} className="btn-edit-account">
            <i className="bi bi-pencil-square" /> Edit Account
          </Link>
        </div>

        {/* ── Tab strip ── */}
        <div className="view-tabs">
          {tabs.map(t => (
            <button
              key={t.id}
              className={`view-tab${currentTab === t.id ? ' active' : ''}`}
              onClick={() => setActiveTab(t.id)}
            >
              <i className={`bi ${t.icon}`} />
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Overview tab ── */}
        {currentTab === 'overview' && (
          <>
            {isReseller ? (
              <div className="view-stats">
                <StatCard label="Total Paid"     value={mask(`€${creditTotals.paid.toFixed(2)}`, 'money')}   icon="bi-cash-stack"     accent="#10b981" glow="rgba(16,185,129,0.3)" />
                <StatCard label="Total Profit"   value={mask(`€${creditTotals.profit.toFixed(2)}`, 'money')} icon="bi-graph-up-arrow" accent="#3b82f6" glow="rgba(59,130,246,0.3)" />
                <StatCard label="Credits Bought" value={mask(creditTotals.credits.toLocaleString(), 'number')}    icon="bi-coin"           accent="#f59e0b" glow="rgba(245,158,11,0.3)" />
                <StatCard label="Purchases"      value={creditPurchases.length}                                   icon="bi-receipt"        accent="#06b6d4" glow="rgba(6,182,212,0.3)"  />
                <StatCard label="Services Sold"  value={creditTotals.services.length}                             icon="bi-diagram-3-fill" accent="#a78bfa" glow="rgba(167,139,250,0.3)" />
              </div>
            ) : (
              <div className="view-stats">
                <StatCard label="Total Revenue"  value={mask(`€${totalRevenue.toFixed(2)}`, 'money')}  icon="bi-cash-stack"         accent="#10b981" glow="rgba(16,185,129,0.3)" />
                <StatCard label="Total Profit"   value={mask(`€${totalProfit.toFixed(2)}`, 'money')}   icon="bi-graph-up-arrow"     accent="#3b82f6" glow="rgba(59,130,246,0.3)" />
                <StatCard label="Transactions"   value={transactions.length}             icon="bi-receipt"            accent="#06b6d4" glow="rgba(6,182,212,0.3)"  />
                <StatCard label="Subscriptions"  value={subscriptions.length}            icon="bi-bookmark-star-fill" accent="#f59e0b" glow="rgba(245,158,11,0.3)" />
                <StatCard label="Referrals"      value={referrals.length}               icon="bi-share-fill"         accent="#a78bfa" glow="rgba(167,139,250,0.3)" />
                {points && (
                  <StatCard label="Available Points" value={mask(points.availablePoints?.toLocaleString() ?? '0', 'number')} icon="bi-star-fill" accent="#4F46E5" glow="rgba(79,70,229,0.3)" />
                )}
              </div>
            )}

            <div className="view-panel">
              <div className="view-panel-header">
                <h6 className="view-panel-title">
                  <i className="bi bi-person-lines-fill" style={{ color: '#3b82f6' }} />
                  Account Information
                </h6>
              </div>
              <div className="info-grid">
                <InfoItem icon="bi-discord"           label="Discord"            value={account.discord} />
                <InfoItem icon="bi-envelope"          label="Email"              value={account.email} />
                <InfoItem icon="bi-hash"              label="Discord ID"         value={account.discordID} />
                <InfoItem icon="bi-send"              label="Telegram"           value={account.telegram ? `@${account.telegram}` : null} />
                <InfoItem icon="bi-123"               label="Telegram ID"        value={account.telegramID} />
                <InfoItem icon="bi-chat-left-dots"    label="Private Channel ID" value={account.discordPrivateChannelID} />
                <InfoItem icon="bi-calendar-plus"     label="Created Date"       value={new Date(Number(account.createdDate)).toLocaleDateString()} />
                <InfoItem icon="bi-person-badge"      label="Account Type"       value={isReseller ? 'Reseller' : 'Customer'} />
              </div>
            </div>
          </>
        )}

        {/* ── Credit Purchases tab (resellers) ── */}
        {currentTab === 'credits' && (
          <div className="view-panel">
            <div className="view-panel-header">
              <h6 className="view-panel-title">
                <i className="bi bi-coin" style={{ color: '#f59e0b' }} />
                Credit Purchases
              </h6>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span className="view-panel-count">{creditPurchases.length}</span>
                <Link
                  to={`/reseller/credits/create?accountId=${account._id}`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 7, border: '1px solid rgba(245,158,11,0.25)', background: 'rgba(245,158,11,0.06)', color: '#fbbf24', fontSize: '0.78rem', fontWeight: 600, textDecoration: 'none' }}
                >
                  <i className="bi bi-plus-lg" /> Log Purchase
                </Link>
              </div>
            </div>
            {creditPurchases.length === 0 ? (
              <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--color-subtle)', fontSize: '0.875rem' }}>
                <i className="bi bi-coin" style={{ fontSize: '1.8rem', display: 'block', marginBottom: 10, opacity: 0.3 }} />
                No credit purchases logged yet
              </div>
            ) : (
              <MaterialReactTable table={creditTable} />
            )}
          </div>
        )}

        {/* ── Services tab (resellers) ── */}
        {currentTab === 'services' && (
          <div className="view-panel">
            <div className="view-panel-header">
              <h6 className="view-panel-title">
                <i className="bi bi-diagram-3-fill" style={{ color: '#06b6d4' }} />
                Services They Sell
              </h6>
              <span className="view-panel-count">{creditTotals.services.length}</span>
            </div>
            {creditTotals.services.length === 0 ? (
              <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--color-subtle)', fontSize: '0.875rem' }}>
                <i className="bi bi-diagram-3" style={{ fontSize: '1.8rem', display: 'block', marginBottom: 10, opacity: 0.3 }} />
                Nothing yet — a service appears here once they buy credits for it
              </div>
            ) : (
              <div style={{ padding: '4px 20px 18px' }}>
                {creditTotals.services.map(code => {
                  const mine = creditPurchases.filter(t => t.resellerCredits?.service === code);
                  const credits = mine.reduce((sum, t) => sum + (t.resellerCredits?.credits || 0), 0);
                  return (
                    <div key={code} style={{ padding: '14px 0', borderBottom: '1px solid rgba(148,163,184,0.12)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                        <ServiceBadge code={code} />
                        <span style={{ fontWeight: 700 }}>Service {code}</span>
                        <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: 'var(--color-muted)' }}>
                          {mask(credits.toLocaleString(), 'number')} credits over {mine.length} purchase{mine.length === 1 ? '' : 's'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {getServiceTiers(code).map(t => (
                          <Badge key={`${t.brand}-${t.tier}`} bg="" style={{ background: t.color, color: '#fff', fontSize: '0.72rem', fontWeight: 600 }}>
                            {t.brand}: {t.tier}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Transactions tab ── */}
        {currentTab === 'transactions' && (
          <div className="view-panel">
            <div className="view-panel-header">
              <h6 className="view-panel-title">
                <i className="bi bi-receipt" style={{ color: '#06b6d4' }} />
                Transaction History
              </h6>
              <span className="view-panel-count">{transactions.length}</span>
            </div>
            <MaterialReactTable table={txTable} />
          </div>
        )}

        {/* ── Subscriptions tab ── */}
        {currentTab === 'subscriptions' && (
          <>
            <div className="view-panel">
              <div className="view-panel-header">
                <h6 className="view-panel-title">
                  <i className="bi bi-bookmark-star-fill" style={{ color: '#f59e0b' }} />
                  IPTV Subscription History
                </h6>
                <span className="view-panel-count">{iptv.length}</span>
              </div>
              <MaterialReactTable table={subTable} />
            </div>

            <div className="view-panel">
              <div className="view-panel-header">
                <h6 className="view-panel-title">
                  <i className="bi bi-phone-fill" style={{ color: '#8B5CF6' }} />
                  App Services
                </h6>
                <span className="view-panel-count">{appSubs.length}</span>
              </div>
              <MaterialReactTable table={appSubTable} />
            </div>
          </>
        )}

        {/* ── Referrals tab ── */}
        {currentTab === 'referrals' && (
          <div className="view-panel">
            <div className="view-panel-header">
              <h6 className="view-panel-title">
                <i className="bi bi-share-fill" style={{ color: '#a78bfa' }} />
                Referral History
              </h6>
              <span className="view-panel-count">{referrals.length}</span>
            </div>
            <MaterialReactTable table={refTable} />
          </div>
        )}

        {/* ── Activity tab (Bot Requests + Issues + Trial Follow-ups) ── */}
        {currentTab === 'activity' && (
          <>
            <div className="view-panel">
              <div className="view-panel-header">
                <h6 className="view-panel-title">
                  <i className="bi bi-inbox-fill" style={{ color: '#60a5fa' }} />
                  Bot Requests
                </h6>
                <span className="view-panel-count">{requests.length}</span>
              </div>
              {!accountData?.account?.discord ? (
                <div style={{ padding: '24px 20px', color: '#475569', fontSize: '0.875rem' }}>
                  <i className="bi bi-discord me-2" />No Discord account linked
                </div>
              ) : (
                <MaterialReactTable table={reqTable} />
              )}
            </div>

            <div className="view-panel">
              <div className="view-panel-header">
                <h6 className="view-panel-title">
                  <i className="bi bi-exclamation-triangle-fill" style={{ color: '#fbbf24' }} />
                  Issues
                </h6>
                <span className="view-panel-count">{issues.length}</span>
              </div>
              {!accountData?.account?.discord ? (
                <div style={{ padding: '24px 20px', color: '#475569', fontSize: '0.875rem' }}>
                  <i className="bi bi-discord me-2" />No Discord account linked
                </div>
              ) : (
                <MaterialReactTable table={issueTable} />
              )}
            </div>

            <div className="view-panel">
              <div className="view-panel-header">
                <h6 className="view-panel-title">
                  <i className="bi bi-cone-striped" style={{ color: '#f59e0b' }} />
                  Trial Follow-ups
                </h6>
                <span className="view-panel-count">{trialFollowups.length}</span>
              </div>
              {!accountData?.account?.discordID ? (
                <div style={{ padding: '24px 20px', color: '#475569', fontSize: '0.875rem' }}>
                  <i className="bi bi-discord me-2" />No Discord ID linked
                </div>
              ) : trialFollowups.length === 0 ? (
                <div style={{ padding: '32px 20px', textAlign: 'center', color: '#94A3B8', fontSize: '0.875rem' }}>
                  <i className="bi bi-inbox" style={{ fontSize: '1.8rem', display: 'block', marginBottom: 10, opacity: 0.3 }} />
                  No trial follow-ups found
                </div>
              ) : (
                <MaterialReactTable table={fuTable} />
              )}
            </div>
          </>
        )}

        {/* ── Points tab ── */}
        {currentTab === 'points' && (
          <div className="view-panel">
            <div className="view-panel-header">
              <h6 className="view-panel-title">
                <i className="bi bi-star-fill" style={{ color: '#4F46E5' }} />
                Points
              </h6>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {points && (
                  <div style={{ display: 'flex', gap: 16, fontSize: '0.78rem' }}>
                    <span style={{ color: '#64748B' }}>
                      Total earned: <strong style={{ color: '#4F46E5' }}>{mask(points.totalPointsEarned?.toLocaleString() ?? '0', 'number')}</strong>
                    </span>
                    <span style={{ color: '#64748B' }}>
                      Available: <strong style={{ color: '#10B981' }}>{mask(points.availablePoints?.toLocaleString() ?? '0', 'number')}</strong>
                    </span>
                    <span style={{ color: '#64748B' }}>
                      Spent: <strong style={{ color: '#EF4444' }}>{mask(((points.totalPointsEarned ?? 0) - (points.availablePoints ?? 0)).toLocaleString(), 'number')}</strong>
                    </span>
                  </div>
                )}
                <Link
                  to={`/points/add?accountId=${account._id}`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 7, border: '1px solid rgba(79,70,229,0.25)', background: 'rgba(79,70,229,0.06)', color: '#4F46E5', fontSize: '0.78rem', fontWeight: 600, textDecoration: 'none' }}
                >
                  <i className="bi bi-plus-lg" /> Adjust
                </Link>
              </div>
            </div>

            {!points ? (
              <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--color-subtle)', fontSize: '0.875rem' }}>
                <i className="bi bi-star" style={{ fontSize: '1.8rem', display: 'block', marginBottom: 10, opacity: 0.3 }} />
                No points yet — use the Adjust button to add some.
              </div>
            ) : claimHistory.length === 0 ? (
              <div style={{ padding: '32px 20px', textAlign: 'center', color: '#94A3B8', fontSize: '0.875rem' }}>
                <i className="bi bi-inbox" style={{ fontSize: '1.8rem', display: 'block', marginBottom: 10, opacity: 0.3 }} />
                No rewards claimed yet
              </div>
            ) : (
              <MaterialReactTable table={claimTable} />
            )}
          </div>
        )}

        {currentTab === 'wallets' && (
          <>
            <div className="view-panel">
              <div className="view-panel-header">
                <h6 className="view-panel-title">
                  <i className="bi bi-wallet2" style={{ color: '#06b6d4' }} />Payment Wallets
                </h6>
                <span className="view-panel-count">{wallets.length}</span>
              </div>

              {wallets.length === 0 ? (
                <div style={{ padding: '32px 20px', textAlign: 'center', color: '#94A3B8', fontSize: '0.875rem' }}>
                  <i className="bi bi-wallet2" style={{ fontSize: '1.8rem', display: 'block', marginBottom: 10, opacity: 0.3 }} />
                  No wallets linked to this account yet
                </div>
              ) : (
                <MaterialReactTable table={walletTable} />
              )}
            </div>

            <div className="view-panel">
              <div className="view-panel-header">
                <h6 className="view-panel-title">
                  <i className="bi bi-receipt-cutoff" style={{ color: '#f59e0b' }} />Invoices
                </h6>
                <span className="view-panel-count">{invoices.length}</span>
              </div>

              {invoices.length === 0 ? (
                <div style={{ padding: '32px 20px', textAlign: 'center', color: '#94A3B8', fontSize: '0.875rem' }}>
                  <i className="bi bi-receipt" style={{ fontSize: '1.8rem', display: 'block', marginBottom: 10, opacity: 0.3 }} />
                  No invoices for this account
                </div>
              ) : (
                <MaterialReactTable table={invoiceTable} />
              )}
            </div>
          </>
        )}

      </div>
    </>
  );
}
